import Breadcrumbs from 'Components/Breadcrumbs'
import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import ListActions from 'Components/ListActions'
import PTAFormField from 'Components/PTAFormField'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { isSuperuser } from 'Services/common/Features/User/userSlice'
import SchoolCalendarDetails from 'Services/SchoolManagement/Features/SchoolCalendar/Components/SchoolCalendarDetails'
import SchoolCalendarList from 'Services/SchoolManagement/Features/SchoolCalendar/Components/SchoolCalendarList'
import useSchoolCalendarsWithParams from 'Services/SchoolManagement/Features/SchoolCalendar/Hooks'
import {
  useDeleteSingleSchoolCalendarMutation,
  useGetSingleSchoolCalendarQuery
} from 'Services/SchoolManagement/Features/SchoolCalendar/schoolCalendarApi'
import { SchoolCalendarTabType } from 'Services/SchoolManagement/Features/SchoolCalendar/types'
import { useTypedSelector } from 'Store'
import { ROWS_PER_PAGE_DEFAULT } from 'Util/constants'
import { useConfirm } from 'Util/hooks/useConfirm'
import { useListFunctions } from 'Util/hooks/useListFunctions'
import SnackbarUtils from 'Util/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Divider, Tab, Typography } from '@mui/material'
import { Box } from '@mui/system'
import { skipToken } from '@reduxjs/toolkit/query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from 'ttag'

function SchoolCalendarListPage() {
  const navigate = useNavigate()
  const { confirm } = useConfirm()
  const { user } = useTypedSelector(state => state.user)
  const { setSelectedAuthorityCode } = useAuthorityContext()

  const {
    schoolCalendars,
    isFetching,
    searchQueryParams,
    setSearchQueryParams,
    totalResults,
    setSelectedSchoolCalendarId,
    selectedSchoolCalendarId
  } = useSchoolCalendarsWithParams()

  const { currentData: singleSchoolCalendar, isFetching: isSingleSchoolCalendarFetching } =
    useGetSingleSchoolCalendarQuery(selectedSchoolCalendarId ?? skipToken)

  // List page can be reached without prior authority; reset so PTABanner reflects no selection
  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress setSelectedAuthorityCode
  useEffect(() => {
    setSelectedAuthorityCode(undefined)
  }, [])

  const [deleteSingleSchoolCalendar, { isLoading: isDeleteLoading }] =
    useDeleteSingleSchoolCalendarMutation()

  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedSchoolCalendarId)

  const breadcrumbs = [
    {
      title: t`Home`,
      link: '/school-management/'
    },
    {
      title: t`Calendars`
    }
  ]

  const { handleChangePage, handleRequestSort, handleCodespaceIdChange } = useListFunctions({
    searchQueryParams,
    setSearchQueryParams
  })

  const onRowClick = (id: string) => {
    setSelectedSchoolCalendarId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setSelectedSchoolCalendarId(undefined)
    setIsDrawerOpen(false)
  }

  const handleDelete = () => {
    if (!selectedSchoolCalendarId) return

    deleteSingleSchoolCalendar(selectedSchoolCalendarId)
      .unwrap()
      .then(() => {
        SnackbarUtils.success(t`Calendar successfully deleted.`)
        handleDrawerClose()
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error deleting the calendar.`)
      })
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs items={breadcrumbs} />
      <Box sx={{ mb: 2 }}>
        <Typography variant="h1" sx={{ mb: 2 }}>{t`Calendars`}</Typography>
        <Box mt={4}>
          <ListActions>
            <CustomButton
              onClick={() => navigate('/school-management/school-calendars/create/')}
              size="small"
              startIcon={<AddIcon />}
            >
              {t`Create new calendar`}
            </CustomButton>
            {isSuperuser(user) && (
              <PTAFormField
                value={searchQueryParams.codespace_id ?? undefined}
                onChange={handleCodespaceIdChange}
                required={false}
                displayEmptyFirstOption={true}
              />
            )}
          </ListActions>
          <Box pt={1}>
            <SchoolCalendarList
              rows={schoolCalendars}
              isLoading={isFetching || isDeleteLoading}
              onRowClick={onRowClick}
              selectedSchoolCalendarId={selectedSchoolCalendarId}
              params={searchQueryParams}
              handleRequestSort={handleRequestSort}
              handleChangePage={handleChangePage}
              count={totalResults}
              page={
                searchQueryParams.start_index
                  ? Number.parseInt(searchQueryParams.start_index, 10) / ROWS_PER_PAGE_DEFAULT
                  : 0
              }
            />
          </Box>
          <DrawerRight
            open={isDrawerOpen}
            onClose={handleDrawerClose}
            topBar={
              <Box>
                <DeleteButton
                  sx={{ mr: 1 }}
                  onClick={() =>
                    confirm({
                      title: t`Are you sure?`,
                      message: t`Are you sure you want to delete the calendar?`,
                      handleConfirm: () => {
                        handleDelete()
                      }
                    })
                  }
                >{t`Delete`}</DeleteButton>
                <CustomButton
                  onClick={() => {
                    setSelectedAuthorityCode(singleSchoolCalendar?.codespace_id)
                    navigate(`/school-management/school-calendars/${selectedSchoolCalendarId}/edit`)
                  }}
                  size="small"
                  startIcon={<EditIcon />}
                >{t`Edit`}</CustomButton>
              </Box>
            }
          >
            <WithLoader loading={isSingleSchoolCalendarFetching || isDeleteLoading}>
              {singleSchoolCalendar && (
                <TabContext value={SchoolCalendarTabType.DETAILS}>
                  <TabList aria-label={t`Details`}>
                    <Tab label={t`Details`} value={SchoolCalendarTabType.DETAILS} />
                  </TabList>
                  <Divider />
                  <TabPanel sx={{ px: 0, pt: 0 }} value={SchoolCalendarTabType.DETAILS}>
                    <SchoolCalendarDetails schoolCalendar={singleSchoolCalendar} />
                  </TabPanel>
                </TabContext>
              )}
            </WithLoader>
          </DrawerRight>
        </Box>
      </Box>
    </Box>
  )
}

export default SchoolCalendarListPage
